/**
 * ToggleRow — a titled setting row with a Toggle on the right — and its
 * tier-locked variant (lock icon, "included in the Enterprise plan" note,
 * owner-only upgrade link, disabled toggle).
 *
 * Extracted from NotificationSettings when Integrations became the second
 * consumer — the same path Toggle itself took out of OrganizationSettings.
 * The locked variant is the org-settings upsell pattern: change it here and
 * every tier-gated feature page follows.
 */

import { Link } from 'react-router-dom';
import { Toggle } from './Toggle';

interface ToggleRowProps {
  title: string;
  description: string;
  enabled: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  /** Suppresses the bottom border on the section's final row. */
  last?: boolean;
}

export function ToggleRow({ title, description, enabled, onChange, disabled, last }: ToggleRowProps) {
  return (
    <div
      className={`flex items-start justify-between py-4 ${
        last ? '' : 'border-b border-gray-100'
      }`}
    >
      <div className="flex-1 pr-4">
        <span className="text-sm font-medium text-gray-900">{title}</span>
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      </div>
      <div className="flex-shrink-0">
        <Toggle enabled={enabled} onChange={onChange} disabled={disabled} label={title} />
      </div>
    </div>
  );
}

interface TierLockedToggleRowProps {
  title: string;
  /** Plural noun phrase for the note: "{featureName} are included in the Enterprise plan." */
  featureName: string;
  /** Owners get the "View plans" link; others just the note. */
  isOrgOwner: boolean;
  last?: boolean;
}

export function TierLockedToggleRow({
  title,
  featureName,
  isOrgOwner,
  last,
}: TierLockedToggleRowProps) {
  return (
    <div
      className={`flex items-start justify-between py-4 ${
        last ? '' : 'border-b border-gray-100'
      }`}
    >
      <div className="flex-1 pr-4">
        <div className="flex items-center">
          <LockIcon className="w-5 h-5 text-gray-400 mr-2" />
          <span className="text-sm font-medium text-gray-500">{title}</span>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          {featureName} are included in the Enterprise plan.{' '}
          {isOrgOwner && (
            <Link
              to="/admin/settings/billing"
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              View plans
            </Link>
          )}
        </p>
      </div>
      <div className="flex-shrink-0">
        <Toggle enabled={false} onChange={() => undefined} disabled label={title} />
      </div>
    </div>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  );
}
